<script setup>
import { ref, watch, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { DxDataGrid } from 'devextreme-vue/data-grid'
import { stringToColor } from '@/lib/utils'

import * as dataGridColumn from '@/composables/data-grid-column'
import { useDataGridWidget } from '@/composables/data-grid-toolbar'
import { useNewBudgetDocumentRow } from '@/composables/budget-document-row'
import { useCommentRowCells } from '@/composables/document-row'
import * as dataGrid from '@/composables/data-grid'

import { useWebSiteStore } from '@/stores/web-site.store'
import { accountingApi } from '@/apis/accounting.api'
import { useAccountingStore } from '@/stores/accounting.store'
import { useDetectDevice } from '@/composables/device'
import { buildTagPayload } from '@/composables/tag'

import FileViewer from '@/components/shared/FileViewer.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import TagCellEditTemplate from '@/components/shared/TagCellEditTemplate.vue'
import BudgetDocumentDetailEdit from '@/components/accounting/BudgetDocumentDetailEdit.vue'
import BudgetDocumentPrintDialog from '@/components/accounting/BudgetDocumentPrintDialog.vue'


const props = defineProps({
  data: {
    type: Object,
    default: null
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  listing: {
    type: Number,
    required: true
  },
  showPrint: {
    type: Boolean,
    default: false
  }
})

const emits = defineEmits(['row-saved', 'grid-ref-set'])
const accountingStore = useAccountingStore()

const budgetDocumentId = props.data?.key
const budgetDocument = ref(null)
const { isMobile } = useDetectDevice()
const dataGridWidget = useDataGridWidget()
const showPrintDialog = ref(false)

onMounted(async () => {
  budgetDocument.value = (await accountingStore.api.getBudgetDocumentsAsync({ id: budgetDocumentId, all: true })).data[0]
  dataGridWidget.printWidgetButton.value?.option('disabled', !budgetDocument.value)
  // Re-evaluate command-button visibility (e.g. delete), which depends on the
  // loaded budget document's is_estimate flag and may render before this resolves.
  gridRef.value?.instance?.repaint()
})

const saveStateId = `budget-document-row-grid`
const gridId = `${saveStateId}_${budgetDocumentId}`

let localizedTags = [...accountingStore.getLocalizedBudgetTags().sort((a, b) => a?.name?.localeCompare(b?.name))]
const getPrimaryTags = () => localizedTags
const dataSource = dataGrid.useODataSource(accountingApi.getAbsoluteUrl() + accountingApi.budgetDocumentRowsUrl)
dataSource.filter([['budget_document', '=', budgetDocumentId], ['is_group_child', '=', false]])

const i18n = getI18n()
const webSiteStore = useWebSiteStore()

const { gridRef } = dataGrid.useGridRef()

watch(gridRef, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    emits('grid-ref-set', newVal)
  }
}, { immediate: false })
const editors = ref({})
const { exportOptions, onExporting } = dataGrid.useDefaultExport({ fileName: `${props.data?.data?.name || budgetDocumentId} - ${i18n.t('common.budgetDocumentRow', 2)}` })
const documentRowType = ref(null)
const currency = webSiteStore.currencies.find(i => i.id === props.data?.data?.currency_ref)

const { newBudgetDocumentRow } = useNewBudgetDocumentRow()

const editItem = ref(null)

const onAddClick = (type = webSiteStore.documentRowTypeEnum.LISTING_ITEM) => {
  editItem.value = newBudgetDocumentRow({
    budgetDocumentId,
    listingId: budgetDocument.value?.listing,
    currencyRef: budgetDocument.value.currency_ref,
    documentRowType: type,
  })
}

const updateGrid = async (saved) => {
  const createdId = dataGrid.getCreatedRowId(editItem.value, saved)
  editItem.value = null
  emits('row-saved', props.data?.row.data.id)
  await updateBudgetTagsAsync()
  await dataGrid.refreshGridAsync(gridRef, createdId)
}

const isInvoiced = (row) => row?.invoice != null

const columns = [
    {
      type: 'buttons',
      showInColumnChooser: false,
      buttons: [
          {
            hint: i18n.t('common.edit'),
            icon: 'edit',
            visible: (e) => !props.readOnly && !e.row?.data?.worksheet_row,
            disabled: (e) => isInvoiced(e.row?.data),
            onClick: (e) => {editItem.value = e.row.data},
          },
          {
            name: 'delete',
            visible: (e) => !e.row?.data?.worksheet_row && (budgetDocument.value?.is_estimate || !e.row?.data?.is_estimate),
            disabled: (e) => isInvoiced(e.row?.data),
          },
      ],
      fixed: false,
      visibleIndex: -1,
      width: '6rem',
    },
    dataGridColumn.useRowNumberColumn(),
    dataGridColumn.useDocumentRowTypeColumn({documentRowType}),
    dataGridColumn.useListingItemColumn({
      listing: props.listing,
      budgetDocument: props.data?.data,
      validationRules: [{
        type: 'custom',
        validationCallback: (e) => {
          return !!e.value || e.data.document_row_type !== webSiteStore.documentRowTypeEnum.LISTING_ITEM
        },
        message: i18n.t('common.required'),
        reevaluate: true,
      }],
      width: '15rem',
    }),
    dataGridColumn.useDescriptionColumn(),
    {
      dataField: 'budget_document_row_category_ref',
      caption: i18n.t('common.category'),
      lookup: dataGrid.useLookup({
        dataSource: {
          store: accountingStore.getLocalizedBudgetDocumentRowCategories(),
        },
        displayExpr: (i) => (i.name),
      }),
      validationRules: [{
        type: 'custom',
        validationCallback: (e) => {
          return !!e.value || e.data.document_row_type !== webSiteStore.documentRowTypeEnum.EXTRA
        },
        message: i18n.t('common.required'),
        reevaluate: true,
      }],
    },
    dataGridColumn.useQuantityColumn(),
    dataGridColumn.useUnitOfMeasureColumn(),
    {
      dataField: 'amount_per_unit',
      caption: i18n.t('common.plannedPerUnitAlt'),
      dataType: 'number',
      format: '#0.##',
      width: '5rem',
      allowEditing: false,
    },
    {
      caption: i18n.t('common.planned'),
      dataType: 'number',
      format: '#0.##',
      width: '5rem',
      allowEditing: false,
      allowSorting: false,
      allowFiltering: false,
      calculateCellValue: (rowData) => {
        const qty = Number(rowData.quantity) || 0
        const apu = Number(rowData.amount_per_unit) || 0
        return apu ? qty * apu : null
      },
    },
    {
      dataField: 'used_total',
      caption: i18n.t('common.used'),
      dataType: 'number',
      format: '#0.##',
      width: '5rem',
      allowEditing: false,
      allowSorting: false,
      allowFiltering: false,
    },
    dataGridColumn.usePriceColumn({ currency }),
    dataGridColumn.useCostColumn({ currency }),
    dataGridColumn.useCostValorizationColumn({ budgetDocument: props.data?.data }),
    dataGridColumn.useDiscountColumn(),
    dataGridColumn.useChargePercentageColumn(),
    dataGridColumn.useVatTypeColumn(),
    dataGridColumn.useTotalNoVatColumn({ currency }),
    dataGridColumn.useTotalVatColumn({ currency }),
    dataGridColumn.useInvoiceColumn(),
    {
      dataField: 'tags',
      caption: i18n.t('common.tag', 2),
      width: '12rem',
      lookup: dataGrid.useLookup({
        dataSource: getPrimaryTags(),
        valueExpr: 'id',
        displayExpr: 'name',
      }),
      setCellValue: (rowData, value) => (rowData.tags = value),
      cellTemplate: 'primary-budget-tags-cell-template',
      editCellTemplate: 'primary-budget-tags-cell-edit-template',
    },
    { dataField: 'row_files', caption: i18n.t('common.attachment', 2), cellTemplate:'fileViewer' },
    dataGridColumn.useNotesColumn({ visible: false }),
]

const updateBudgetTagsAsync = async() => {
  await accountingStore.fetchBudgetTagsAsync({all: true}, true)
  localizedTags = [...accountingStore.getLocalizedBudgetTags().sort((a, b) => a?.name?.localeCompare(b?.name))]
  togglePrimaryTagDropDown.value.option('items', getToggleTagButtonUpdateItems())
  editors.value['tags']?.option('dataSource', getPrimaryTags())
}

const onSaved = async() => {
  emits('row-saved', props.data?.row.data.id)
}


const getChips = (tags) => {
  return tags.map(tag => {
    const name = localizedTags.find(t => t.id === tag)?.name
    return {
      id: tag,
      name: name,
      color: stringToColor(name),
    }
  })
}

const onCustomItemCreating = async(args) => {
  if (!args.text) {
    args.customItem = null
    return
  }
  const itemName = args.text.trim()
  const itemInDataSource = getPrimaryTags().find((item) => item.name === itemName)
  if (itemInDataSource) {
    args.customItem = itemInDataSource
  } else {
    const newBudgetTag = buildTagPayload(itemName, { name: itemName })
    args.customItem = new Promise((resolve) => {
      (async () => {
        await accountingStore.addBudgetTagAsync(newBudgetTag)
        await updateBudgetTagsAsync()
        const currentItem = localizedTags.find((item) => item.name === itemName)
        resolve({ id: currentItem.id, name: currentItem.name })
      })()
    })
  }
}
const onPrimaryCustomItemCreating = (args) => onCustomItemCreating(args)

const { onDragEnd } = dataGrid.useDefaultRowReordering(accountingApi.reorderBudgetDocumentRowsAsync, budgetDocumentId, gridRef)

const togglePrimaryTagDropDown = ref(null)

const getToggleTagButtonUpdateItems = () => {
  return getPrimaryTags().map(tag => ({
    id: tag.id,
    name: tag.name,
    onClick: async() => {
      const selectedRows = gridRef.value.instance.getSelectedRowsData()
      if (selectedRows.length !== 0) {
        await accountingStore.updateTagBudgetDocumentRowsAsync(tag.id, { budget_document_row_ids: selectedRows.map(i => i.id) })
        await updateBudgetTagsAsync()
        gridRef.value.instance.refresh()
      }
    }
  }))
}

const togglePrimaryTagButton = () => ({
  location: 'after',
  widget: 'dxDropDownButton',
  options: {
    text: i18n.t('common.togglePrimaryTag'),
    icon: 'mdi mdi-tag',
    items: getToggleTagButtonUpdateItems(),
    displayExpr: 'name',
    valueExpr: 'id',
    useSelectMode: false,
    disabled: true,
    onInitialized: (args) => { togglePrimaryTagDropDown.value = args.component },
  }
})

const printWidgets = () => props.showPrint
  ? [dataGridWidget.printWidget({
    disabled: budgetDocument.value === null,
    onClick: () => { showPrintDialog.value = true },
  })]
  : []

const onSelectionChanged = (e) => {
  togglePrimaryTagDropDown.value.option('disabled', e.selectedRowsData.length === 0)
}


const onDownloadRowFileAsync = async(e) => {
  if (e) {
    await accountingStore.api.downloadBudgetDocumentRowFileAsync(e.id, e.name)
  }
}

const { clearCommentRowPricingCell } = useCommentRowCells()

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  saveStateId: saveStateId,
  mode: 'row',
  dataSource: dataSource,
  columns: columns,
  editing: dataGrid.useDefaultEditing(),
  selection: { mode: 'multiple', showCheckBoxesMode: 'onClick', selectAllMode: 'page' },
  onSaved: onSaved,
  onSelectionChanged: onSelectionChanged,
  onExporting: onExporting,
  export: exportOptions,
  exportWidgets: printWidgets(),
  dataActionWidgets: [togglePrimaryTagButton()],
  onCellPrepared: clearCommentRowPricingCell,
  onEditingStart: (e) => { e.cancel = isInvoiced(e.data) },
  rowDragging: {
    allowReordering: true,
    onDragEnd: onDragEnd
  },
  addButtons: [
    {
      label: i18n.t('common.documentRowTypes.comment'),
      icon: 'mdi-comment-plus-outline',
      visible: !props.readOnly,
      onClick: () => onAddClick(webSiteStore.documentRowTypeEnum.COMMENT),
    },
    {
      label: i18n.t('common.documentRowTypes.extra'),
      icon: 'mdi-plus',
      visible: !props.readOnly,
      onClick: () => onAddClick(webSiteStore.documentRowTypeEnum.EXTRA),
    },
  ],
  addButton: { onClick: () => onAddClick() },
  isDetailGrid: true,
})

</script>
<template>
  <div>
    <q-dialog v-model="showPrintDialog" :maximized="isMobile" persistent>
      <budget-document-print-dialog
        v-if="showPrintDialog && budgetDocument !== null"
        :budget-document-id="budgetDocument.id"
        @cancel="showPrintDialog = false"
      />
    </q-dialog>
    <q-dialog
      :model-value="editItem !== null"
      class="dialog-md"
      persistent
      :maximized="isMobile"
    >
      <budget-document-detail-edit
        v-if="editItem !== null"
        :model-value="editItem"
        :budget-document="props.data?.data"
        @update="updateGrid"
        @cancel="updateGrid"
      />
    </q-dialog>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #primary-budget-tags-cell-template="{ data: templateOptions }">
        <chip-template
          v-if="templateOptions.data.tags && templateOptions.data.tags.length && templateOptions.data.tags.every(tag => tag !== null)"
          :values="getChips(templateOptions.data.tags)"
        />
      </template>
      <template #primary-budget-tags-cell-edit-template="{ data: templateOptions }">
        <tag-cell-edit-template
          :value="templateOptions.data.tags"
          :data-source="getPrimaryTags()"
          :accept-custom-value="true"
          @initialized="(e) => editors['tags'] = e.component"
          @custom-item-creating="onPrimaryCustomItemCreating"
          @value-changed="(e) => templateOptions.setValue(e.value)"
        />
      </template>
      <template #fileViewer="{ data: templateOptions }">
        <file-viewer
          :cell-info="templateOptions"
          :urls="templateOptions.data.row_urls"
          dense
          @download="onDownloadRowFileAsync"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
<style>
</style>
